import React, { useEffect, useState } from 'react'
import Fade from '@mui/material/Fade'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined'
import MailOutlinedIcon from '@mui/icons-material/MailOutlined'
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined'
import Box from '@mui/material/Box'
import { useTranslation } from 'react-i18next'
import { SxProps } from '@mui/material'

import ListItemButton from 'components/Sidebar/Styled/Button'
import ListItemIcon from 'components/Sidebar/Styled/ListItemIcon'
import Help from 'components/Sidebar/DrawerFooter/Help'
import Logout from 'components/Sidebar/Logout'
import { useAppSelector } from 'redux/Hook'
import { PlatformAdmin } from 'lib/authorization/Roles'
import bluehavenDomain from 'lib/utility/BluehavenDomain'
import ChangeLocale from 'containers/sidebar/ChangeLocale'
import { renderIfAllowed } from 'lib/authorization/AuthorizeComponent'

const isSuperAdmin = (userRole: string) => {
  return PlatformAdmin === userRole
}

type FooterProps = {
  isDrawerVisible: boolean
  collapsedWidth: string
  width: string
}

type ProfileOptionsProps = {
  isPopperVisible: boolean
  anchorEl: null | HTMLElement
  onMobile?: boolean
}

const ProfileOptions = ({
  isPopperVisible,
  anchorEl,
  onMobile,
}: ProfileOptionsProps) => {
  const { t } = useTranslation()
  const { roleName, id } = useAppSelector((state) => state.user.user)
  const isNotAPlatformAdmin = !isSuperAdmin(roleName)

  const getPopperPosition = () => {
    if (onMobile) {
      return 'top-start'
    }

    return isSuperAdmin(roleName) ? 'right-end' : 'right'
  }

  const styles: SxProps = onMobile ? { zIndex: 1299, mr: 1 } : { zIndex: 2 }

  return (
    <Popper
      open={isPopperVisible}
      anchorEl={anchorEl}
      placement={getPopperPosition()}
      transition
      sx={styles}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={350}>
          <Paper
            sx={{
              marginLeft: '10px',
              marginTop: isNotAPlatformAdmin ? '0px' : '40px',
            }}
          >
            <List component="div" disablePadding>
              {isNotAPlatformAdmin && (
                <ListItemButton
                  href={`${bluehavenDomain()}/admin/people/administrators/${id}`}
                  component="a"
                  key="profile"
                >
                  <PersonOutlineOutlinedIcon />
                  <ListItemText primary={t('sidebar.profile')} />
                </ListItemButton>
              )}
              {isNotAPlatformAdmin && (
                <ListItemButton
                  key="email-subscriptions"
                  href={`${bluehavenDomain()}/admin/subscriptions`}
                  component="a"
                >
                  <MailOutlinedIcon />
                  <ListItemText primary={t('sidebar.emailSubscription')} />
                </ListItemButton>
              )}
              {renderIfAllowed(
                'multilingual_admin_interface',
                <ListItem>
                  <ChangeLocale />
                </ListItem>,
              )}
              <ListItem>
                <Logout />
              </ListItem>
            </List>
          </Paper>
        </Fade>
      )}
    </Popper>
  )
}

const Footer = ({ isDrawerVisible, collapsedWidth, width }: FooterProps) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [isPopperVisible, setPopperVisibility] = useState<boolean>(false)

  useEffect(() => {
    if (!isDrawerVisible) {
      setPopperVisibility(false)
    }
  }, [isDrawerVisible])

  const { login } = useAppSelector((state) => state.user.user)

  const handleMenuClick = (event: any) => {
    setAnchorEl(event.currentTarget)
    setPopperVisibility(!isPopperVisible)
  }

  return (
    <Box
      sx={{
        backgroundColor: '#fafafa',
        minHeight: '128px',
      }}
      id="footer"
    >
      <List>
        <ListItemButton
          onClick={(event: any) => handleMenuClick(event)}
          data-testid="account"
          sx={{ width: isDrawerVisible ? width : collapsedWidth }}
        >
          <ListItemIcon>
            <AccountCircleIcon />
          </ListItemIcon>

          <ListItemText
            primary={login}
            sx={{
              whiteSpace: 'break-spaces',
              ...(isDrawerVisible && { wordBreak: 'break-word' }),
            }}
          />
          <ChevronRightOutlinedIcon />
          <ProfileOptions
            anchorEl={anchorEl}
            isPopperVisible={isPopperVisible}
          />
        </ListItemButton>
        <Box></Box>
        <Help />
      </List>
    </Box>
  )
}

export default Footer
export { ProfileOptions }
